import { CROP_ASPECT } from './constants';

/**
 * Pure math functions for the zoom/pan crop editor.
 *
 * Terminology:
 *   frame     -- fixed rectangle on screen with target aspect ratio
 *   baseScale -- scale factor so that the image exactly covers the frame (like object-fit: cover)
 *   zoom      -- multiplier on top of baseScale; zoom=1 means full image visible (cover)
 *   pan       -- pixel offset of image center relative to frame center
 *   source    -- original image pixel coordinates (what the backend operates on)
 */

/** Padding subtracted from container to keep handles/rotation zones visible */
const FRAME_PADDING = 60;

/**
 * Largest rectangle of the target aspect ratio that fits inside `containerW x containerH`.
 *
 * @param {number} containerW
 * @param {number} containerH
 * @param {number|boolean} aspect - numeric aspect ratio (w/h), or boolean isPortrait for legacy compat
 * @returns {{ frameW: number, frameH: number }}
 */
export function computeFrameDimensions(containerW, containerH, aspect) {
  // Legacy boolean support: true -> 2:3, false -> 3:2
  let numericAspect;
  if (typeof aspect === 'boolean') {
    numericAspect = aspect ? 1 / CROP_ASPECT : CROP_ASPECT;
  } else {
    numericAspect = aspect;
  }
  // Apply padding so handles and rotation zones stay within the visible container
  const padW = Math.max(100, containerW - FRAME_PADDING);
  const padH = Math.max(100, containerH - FRAME_PADDING);
  let frameW = padW;
  let frameH = frameW / numericAspect;
  if (frameH > padH) {
    frameH = padH;
    frameW = frameH * numericAspect;
  }
  return { frameW, frameH };
}

/**
 * Scale factor so the image exactly covers the frame (object-fit: cover logic).
 *
 * @param {number} frameW
 * @param {number} frameH
 * @param {number} imgNaturalW
 * @param {number} imgNaturalH
 * @returns {number}
 */
export function computeBaseScale(frameW, frameH, imgNaturalW, imgNaturalH) {
  return Math.max(frameW / imgNaturalW, frameH / imgNaturalH);
}

/**
 * Minimum zoom so the (optionally rotated) image still covers the frame.
 * At minZoom the user sees the widest possible crop.
 *
 * @param {number} frameW
 * @param {number} frameH
 * @param {number} imgNaturalW
 * @param {number} imgNaturalH
 * @param {number} rotationDeg
 * @returns {number} >= 1.0
 */
export function computeMinZoom(frameW, frameH, imgNaturalW, imgNaturalH, rotationDeg) {
  const rad = Math.abs(rotationDeg) * (Math.PI / 180);
  if (rad < 0.001) return 1.0;

  const sinA = Math.sin(rad);
  const cosA = Math.cos(rad);

  // The rotated image's bounding-box shrinks relative to the original.
  // We need to find the factor by which the usable area shrinks.
  // For a rectangle w x h rotated by angle a, the largest axis-aligned
  // rectangle that fits inside has dimensions:
  //   rW = w*cos - h*sin  (can go negative for large angles)
  //   rH = h*cos - w*sin
  // We need the image (at baseScale * zoom) to still cover the frame.
  // So the effective image coverage at zoom=z is:
  //   coverW = imgNaturalW * baseScale * z * cosA - imgNaturalH * baseScale * z * sinA
  //   coverH = imgNaturalH * baseScale * z * cosA - imgNaturalW * baseScale * z * sinA
  // We need coverW >= frameW and coverH >= frameH.

  const base = computeBaseScale(frameW, frameH, imgNaturalW, imgNaturalH);
  const effW = imgNaturalW * base;
  const effH = imgNaturalH * base;

  // Rotated bounding box of the frame that the image must cover
  const neededW = frameW * cosA + frameH * sinA;
  const neededH = frameH * cosA + frameW * sinA;

  const zoomW = neededW / effW;
  const zoomH = neededH / effH;

  return Math.max(1.0, zoomW, zoomH);
}

/**
 * Clamp pan so the image always fully covers the frame.
 *
 * @param {number} panX
 * @param {number} panY
 * @param {number} zoom
 * @param {number} baseScale
 * @param {number} frameW
 * @param {number} frameH
 * @param {number} imgNaturalW
 * @param {number} imgNaturalH
 * @param {number} rotationDeg
 * @returns {{ panX: number, panY: number }}
 */
export function clampPan(panX, panY, zoom, baseScale, frameW, frameH, imgNaturalW, imgNaturalH, rotationDeg) {
  const s = baseScale * zoom;
  const scaledW = imgNaturalW * s;
  const scaledH = imgNaturalH * s;

  const rad = (rotationDeg * Math.PI) / 180;
  const sinA = Math.sin(rad);
  const cosA = Math.cos(rad);

  // The valid pan region for non-zero rotation is a parallelogram, not a
  // rectangle, in (panX, panY) space. The bounds are axis-aligned in rotated
  // coordinates (u, v) = (panX·cos α + panY·sin α, panX·sin α − panY·cos α).
  // Clamping (panX, panY) directly to a rectangle lets the frame's corners
  // exit the rotated image content (visible as a black wedge in the corners).
  const effectiveFrameW = frameW * Math.abs(cosA) + frameH * Math.abs(sinA);
  const effectiveFrameH = frameH * Math.abs(cosA) + frameW * Math.abs(sinA);
  const maxU = Math.max(0, (scaledW - effectiveFrameW) / 2);
  const maxV = Math.max(0, (scaledH - effectiveFrameH) / 2);

  // M = [[cos α, sin α], [sin α, −cos α]] is involutory (M·M = I), so we use
  // the same matrix to map back. At α = 0: (u, v) = (panX, −panY) and we
  // recover the original axis-aligned clamp.
  const u = panX * cosA + panY * sinA;
  const v = panX * sinA - panY * cosA;
  const cu = Math.max(-maxU, Math.min(maxU, u));
  const cv = Math.max(-maxV, Math.min(maxV, v));

  return {
    panX: cu * cosA + cv * sinA,
    panY: cu * sinA - cv * cosA,
  };
}

/**
 * Convert zoom/pan state to source-space XYXY crop coordinates.
 *
 * The frame shows a window into the image. We reverse-transform the
 * frame corners through the pan + scale to get source-space coordinates.
 *
 * @param {number} zoom
 * @param {number} panX
 * @param {number} panY
 * @param {number} baseScale
 * @param {number} frameW
 * @param {number} frameH
 * @param {number} imgNaturalW
 * @param {number} imgNaturalH
 * @param {number} sourceW - original image width
 * @param {number} sourceH - original image height
 * @returns {number[]} [x1, y1, x2, y2] in source-space (XYXY)
 */
export function zoomPanToSourceCrop(zoom, panX, panY, baseScale, frameW, frameH, imgNaturalW, imgNaturalH, sourceW, sourceH) {
  const s = baseScale * zoom;

  // Image center is at frame center + pan offset
  // Frame top-left in image-pixel space:
  //   The frame center is at (frameW/2, frameH/2) in frame coords.
  //   The image center is displayed at (frameW/2 + panX, frameH/2 + panY) in frame coords.
  //   So a frame point (fx, fy) maps to image-natural coord:
  //     imgX = (fx - frameW/2 - panX) / s + imgNaturalW/2
  //     imgY = (fy - frameH/2 - panY) / s + imgNaturalH/2

  const imgX1 = (-frameW / 2 - panX) / s + imgNaturalW / 2;
  const imgY1 = (-frameH / 2 - panY) / s + imgNaturalH / 2;
  const imgX2 = (frameW / 2 - panX) / s + imgNaturalW / 2;
  const imgY2 = (frameH / 2 - panY) / s + imgNaturalH / 2;

  // Scale from natural (viz preview) space to source (original) space
  const scaleToSourceX = sourceW / imgNaturalW;
  const scaleToSourceY = sourceH / imgNaturalH;

  const x1 = Math.max(0, Math.round(imgX1 * scaleToSourceX));
  const y1 = Math.max(0, Math.round(imgY1 * scaleToSourceY));
  const x2 = Math.min(sourceW, Math.round(imgX2 * scaleToSourceX));
  const y2 = Math.min(sourceH, Math.round(imgY2 * scaleToSourceY));

  return [x1, y1, x2, y2];
}

/**
 * Initialize zoom/pan from a saved source-space XYXY crop.
 *
 * @param {number[]} cropXYXY - [x1, y1, x2, y2] in source-space
 * @param {number} sourceW - original image width
 * @param {number} sourceH - original image height
 * @param {number} imgNaturalW - viz preview natural width
 * @param {number} imgNaturalH - viz preview natural height
 * @param {number} baseScale
 * @param {number} frameW
 * @param {number} frameH
 * @returns {{ zoom: number, panX: number, panY: number }}
 */
export function sourceCropToZoomPan(cropXYXY, sourceW, sourceH, imgNaturalW, imgNaturalH, baseScale, frameW, frameH) {
  const [sx1, sy1, sx2, sy2] = cropXYXY;

  // Convert source-space crop to natural-pixel (viz) space
  const scaleFromSourceX = imgNaturalW / sourceW;
  const scaleFromSourceY = imgNaturalH / sourceH;

  const natX1 = sx1 * scaleFromSourceX;
  const natY1 = sy1 * scaleFromSourceY;
  const natX2 = sx2 * scaleFromSourceX;
  const natY2 = sy2 * scaleFromSourceY;

  const cropNatW = natX2 - natX1;
  const cropNatH = natY2 - natY1;

  // The zoom is determined by how much of the image is visible.
  // At zoom=1 (with baseScale), the frame shows frameW/baseScale natural pixels wide.
  // The crop shows cropNatW natural pixels wide.
  // So: frameW / (baseScale * zoom) = cropNatW
  //     zoom = frameW / (baseScale * cropNatW)
  // Same for height; take the larger zoom (tighter fit) to ensure frame is covered.
  const zoomFromW = frameW / (baseScale * cropNatW);
  const zoomFromH = frameH / (baseScale * cropNatH);
  const zoom = Math.max(zoomFromW, zoomFromH);

  // Pan: the center of the crop in natural space relative to image center
  const cropCenterNatX = (natX1 + natX2) / 2;
  const cropCenterNatY = (natY1 + natY2) / 2;
  const imgCenterNatX = imgNaturalW / 2;
  const imgCenterNatY = imgNaturalH / 2;

  // panX = offset in screen pixels from frame center to image center
  // Image center in frame coords: frameW/2 + panX
  // Crop center should be at frame center, so:
  //   (cropCenterNatX - imgCenterNatX) * baseScale * zoom = -panX
  const s = baseScale * zoom;
  const panX = -(cropCenterNatX - imgCenterNatX) * s;
  const panY = -(cropCenterNatY - imgCenterNatY) * s;

  return { zoom, panX, panY };
}
